import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Brain,
  Leaf,
UserSquare2,
  Dna,
  Shirt,
  ShieldAlert,
  ArrowRight,
  Sparkles,
  Zap,
  Eye,
  TrendingUp,
  CheckCircle,
  Bot,
  Cpu,
  Network,
  Activity,
  CandlestickChart,
  ShieldCheck,
  Building2
} from 'lucide-react';

/**
 * Lightweight image component with fallback handling.
 * Keeps everything in a single file so you can drop this straight in.
 */
const ImageWithFallback: React.FC<{
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
}> = ({ src, alt, className, fallbackSrc }) => {
  const [imgSrc, setImgSrc] = useState(src);
  return (
    <img
      loading="lazy"
      src={imgSrc}
      alt={alt}
      className={className}
      onError={() => {
        if (fallbackSrc && imgSrc !== fallbackSrc) setImgSrc(fallbackSrc);
      }}
    />
  );
};

interface AIProduct {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ComponentType<any>;
  logo?: string;
  features: string[];
  benefits: string[];
  path: string;
  gradient: string;
  imageUrl: string;
  imageAlt?: string;
}


const aiProducts: AIProduct[] = [
  {
    id: 'cryptoTradingPlatform',
    title: 'Crypto Trading Platform',
    subtitle: 'Real Time Trading, Analytics, and Risk Management',
    description:
      'A secure, high performance platform for digital asset trading. It delivers real time market data, advanced order execution, portfolio management, and risk controls. Built for investors, traders, and institutions with strong security, compliance, and developer APIs.',
    icon: CandlestickChart,
    features: [
      'Real time market data, order book, and depth charts',
      'Advanced order types including market, limit, stop, and OCO',
      'Smart order routing and execution quality analytics',
      'Interactive charting with technical indicators and alerts',
      'Portfolio, balances, PnL, and performance tracking',
      'Risk engine for margin, liquidation, and position limits',
      'Secure wallets with multi sig or MPC integrations',
      'KYC, AML, and KYT workflows with audit logs',
      'REST and WebSocket APIs for algorithmic trading',
      'Mobile and web apps with push notifications'
    ],
    benefits: [
      'Improves execution quality and price discovery',
      'Reduces operational risk with strong controls',
      'Increases user retention through advanced tools',
      'Supports compliance with built in workflows',
      'Accelerates development with robust APIs'
    ],
    path: '/products/ai/crypto-trading-platform',
    gradient: 'from-indigo-500 to-purple-600',
    imageUrl: '/assets/Products/ai-Products/CryptoTrading.png',
    imageAlt: 'Crypto trading dashboard with candlestick chart, order book, and positions panel'
  },
  {
    id: 'financePolicyCheck',
    title: 'Finance Policy Check',
    subtitle: 'Automated Policy Compliance and Spend Control',
    description:
      'A centralized platform that automates finance policy checks across expenses, procurement, and payments. It validates transactions in real time against company rules and regulatory requirements, flags exceptions, routes approvals, and maintains a complete audit trail for internal and external audits.',
    icon: ShieldCheck,
    features: [
      'Policy catalog with version control and attestation',
      'Rules engine for real time validation of expenses and purchases',
      'Risk scoring and priority queues for reviewers',
      'Pre approval workflows and conditional approvals',
      'Exception handling with evidence capture and resolution notes',
      'Segregation of duties checks across roles and workflows',
      'Audit trail with time stamps, user actions, and rationale',
      'Analytics and dashboards for violations, savings, and cycle time',
      'Exports for auditors and regulators in standard formats',
      'Integrations with ERP, HR, procurement, and card platforms',
      'APIs and webhooks for custom controls and alerts'
    ],
    benefits: [
      'Reduces policy violations and spend leakage',
      'Improves audit readiness with complete evidence',
      'Shortens approval cycles and increases accuracy',
      'Provides transparency and accountability across teams',
      'Supports regulatory compliance with consistent checks'
    ],
    path: '/products/ai/finance-policy-check',
    gradient: 'from-emerald-500 to-teal-600',
    imageUrl: '/assets/Products/ai-Products/FinancePolicy.png',
    imageAlt: 'Finance policy compliance dashboard with rules, alerts, approvals, and audit trail'
  },
  {
    id: 'geneSeqer',
    title: 'GeneSeqer RNA-Seq Platform',
    subtitle: 'R Shiny ML Platform for Biologists',
    description:
      'GeneSeqer is an R Shiny machine learning platform designed for biologists. Users can upload FASTQ files and receive differential expression results, ML and NLP insights, and interactive visualizations without writing code. The guided interface removes bioinformatics bottlenecks and accelerates bench-to-bedside discovery.',
    icon: Dna,
    logo: '',
    features: [
      'FASTQ upload with guided analysis flow',
      'Automated differential expression results',
      'Machine learning and NLP insights',
      'Interactive visualizations such as heatmaps and volcano plots',
      'No-code, user-friendly R Shiny interface',
      'Exportable, publication-ready figures'
    ],
    benefits: [
      'Accelerate discovery by removing bioinformatics bottlenecks',
      'Make advanced RNA-Seq analysis accessible to non-programmers',
      'Improve decision making with ML-augmented insights',
      'Streamline workflows with an intuitive guided UI'
    ],
    path: '/products/ai/geneseqer',
    gradient: 'from-emerald-500 to-teal-600',
    imageUrl: '/assets/Products/ai-Products/geneSeqer.png',
    imageAlt: 'GeneSeqer RNA-Seq analysis platform showing interactive visualizations'
  },
  {
    id: 'greenH2',
    title: 'Green H₂',
    subtitle: 'Sustainable Hydrogen Technology Framework',
    description:
      'Green H₂ provides a comprehensive framework for advancing green hydrogen technologies through sustainability, technical innovation, and integrated project delivery. It supports global decarbonization goals by enabling clean energy production, storage, and utilization across industries.',
    icon: Leaf,
    logo: '',
    features: [
      'Electrolysis systems for clean hydrogen generation',
      'Hydrogen storage, transport, and ammonia integration',
      'Strategic partnerships and infrastructure alignment',
      'Feasibility studies, engineering design, and installation',
      'Lifecycle management from design to deployment'
    ],
    benefits: [
      'Promotes clean and renewable energy alternatives',
      'Supports global decarbonization and net zero targets',
      'Encourages innovation through prototyping and testing',
      'Ensures reliable, scalable, and efficient project delivery',
      'Integrates sustainability into every phase of development'
    ],
    path: '/products/greenh2',
    gradient: 'from-green-500 to-emerald-600',
    imageUrl: '/assets/Products/ai-Products/greenH2.png',
    imageAlt: 'Green hydrogen production plant with electrolyzers and storage tanks'
  },
  {
    id: 'LearningManagementSystem',
    title: 'Learning Management System',
    subtitle: 'Mobile First, Multi Campus, Secure',
    description:
      'An AI powered learning platform designed for rapid multi campus rollout. It features a mobile first interface, integrated fee billing, AI tutors, auto generated flashcards, aptitude drills, and career path hints to personalize learning and improve outcomes. Granular role based access control ensures the right resources for each user.',
    icon: Brain,
    logo: '',
    features: [
      'Mobile first interface for learners and faculty',
      'Rapid multi campus deployment with centralized administration',
      'AI tutors for contextual help and study guidance',
      'Auto generated flashcards and aptitude drills',
      'Career path hints based on learner progress',
      'Integrated fee billing and payment tracking',
      'Granular role based access control with RBAC',
      'Analytics on engagement, outcomes, and completion rates',
      'Personalized learning paths and content recommendations'
    ],
    benefits: [
      'Improves learning outcomes with personalized support',
      'Boosts engagement through interactive AI driven study tools',
      'Speeds rollout across campuses with centralized controls',
      'Reduces administrative overhead with integrated billing',
      'Strengthens security and compliance with granular RBAC'
    ],
    path: '/products/ai/LearningManagementSystem',
    gradient: 'from-orange-500 to-red-600',
    imageUrl: '/assets/Products/ai-Products/LearningManagementSystem.png',
    imageAlt: 'AI powered learning platform on mobile and web with analytics, billing, and tutoring'
  },
  {
    id: 'mvsxAi',
    title: 'MVS x AI',
    subtitle: 'Computer Vision Safety Guard',
    description:
      'MVS x AI is a computer vision safety guard that analyzes camera feeds 24x7 to detect missing or incorrect safety gear and trigger instant alerts. It is built for industrial sites to enforce safety protocols, reduce incidents, and minimize regulatory risk. The system continuously learns to improve accuracy under varying lighting and environmental conditions.',
    icon: ShieldAlert,
    logo: '',
    features: [
      'Real time analysis of live video feeds',
      'Multi PPE detection for helmets, gloves, masks, and jackets',
      'Instant non compliance alerts with escalation rules',
      'Automated violation logging with timestamps and snapshots',
      'Compliance reporting dashboard for audits and trends',
      'Continuous learning for changing lighting and environments',
      'Deployment ready for edge and cloud cameras'
    ],
    benefits: [
      'Reduce workplace incidents with faster response',
      'Ensure consistent safety protocol compliance',
      'Gain audit ready logs and reports',
      'Improve visibility across high risk zones',
      'Adapt to on site conditions through continuous learning'
    ],
    path: '/products/ai/mvsxAi',
    gradient: 'from-green-500 to-emerald-600',
    imageUrl: '/assets/Products/ai-Products/mvsxAi.png',
    imageAlt: 'AI vision system monitoring PPE compliance on an industrial site'
  },
  {
    id: 'panaceon',
    title: 'Panaceon Medical Coding',
    subtitle: 'Autonomous Agentic AI for Clinical Coding',
    description:
      'Panaceon uses autonomous agentic AI to process clinical charts and run ICD-10, CPT, and HCPCS coding agents. It suggests accurate codes with audit-ready citations, automatically approves high-confidence cases, and routes exceptions for human review. This reduces manual errors and ensures billing-ready, compliant claims.',
    icon: Brain,
    logo: '',
    features: [
      'Autonomous ICD-10, CPT, and HCPCS coding agents',
      'Per-chart confidence scoring with automatic approval',
      'Exception routing to human reviewers',
      'Audit-ready citations for each code',
      'Live KPIs on throughput, rework, and DNFB',
      'Streamlined workflow that reduces rework'
    ],
    benefits: [
      'High compliance with billing-ready claims',
      'Significantly fewer manual errors',
      'Improved first-pass quality',
      'Faster and more reliable claims processing',
      'Operational visibility through real-time KPIs'
    ],
    path: '/products/ai/Panaceon',
    gradient: 'from-green-500 to-emerald-600',
    imageUrl: '/assets/Products/ai-Products/panaceon.png',
    imageAlt: 'Autonomous medical coding agents processing clinical charts'
  },
  {
    id: 'planfusion',
    title: 'Planfusion',
    subtitle: 'AI Powered Lead Management',
    description:
      'An AI driven platform that captures, scores, routes, and nurtures leads across channels. It automates follow ups, prioritizes high intent prospects, and unifies data with your CRM to increase conversion and shorten response time.',
    icon: Bot,
    logo: '',
    features: [
      'Multichannel lead capture from web, ads, email, and offline sources',
      'AI lead scoring based on behavior, fit, and intent signals',
      'Automated outreach sequences with emails, calls, and tasks',
      'Smart routing and assignment by territory, availability, and priority',
      'Duplicate detection and lead enrichment for cleaner data',
      'Real time dashboards for status, conversion, velocity, and SLA alerts',
      'Native CRM integrations for unified pipeline visibility'
    ],
    benefits: [
      'Higher conversion through fast, prioritized follow up',
      'Lower manual effort with automated nurturing and routing',
      'Aligned sales and marketing through shared visibility and goals',
      'Better forecasting with accurate scoring and clean data',
      'Shorter sales cycles with AI assisted next best actions'
    ],
    path: '/products/ai/Planfusion',
    gradient: 'from-yellow-500 to-red-600',
    imageUrl: '/assets/Products/ai-Products/planfusion.png',
    imageAlt: 'AI powered lead management with workflows, scoring, and dashboards'
  },
  /* hidden – Predictive Maintenance
  {
    id: 'predictiveMaintenance',
    title: 'Predictive Maintenance',
    subtitle: 'AI Powered Equipment Monitoring and Failure Prevention',
    description:
      'An AI powered predictive maintenance platform that analyzes sensor data to identify early signs of equipment wear or failure. It learns normal operating patterns, detects anomalies in real time, and schedules maintenance before breakdowns occur, reducing downtime and maximizing asset performance.',
    icon: Activity,
    logo: '',
    features: [
      'Continuous monitoring of temperature, vibration, and pressure sensors',
      'Machine learning models that learn normal equipment behavior',
      'Real time anomaly detection and failure prediction',
      'Automated maintenance scheduling based on condition data',
      'Historical trend analysis and performance visualization',
      'Integration with existing maintenance management systems'
    ],
    benefits: [
      'Reduce unplanned downtime and maintenance costs',
      'Prevent unexpected equipment failures',
      'Extend asset lifespan through proactive servicing',
      'Optimize maintenance schedules using AI insights',
      'Increase operational efficiency and reliability'
    ],
    path: '/products/ai/predictiveMaintenance',
    gradient: 'from-orange-500 to-red-600',
    imageUrl: '/assets/Products/ai-Products/predictiveMaintenance.png',
    imageAlt: 'AI powered predictive maintenance system monitoring industrial equipment'
  },
  */ {
    id: 'realEstateAIPlatform',
    title: 'Real Estate AI Platform',
    subtitle: 'Recommendations, Valuation, and Operations Intelligence',
    description:
      'An AI powered real estate platform for developers, brokers, and property managers. It delivers property recommendations, automated valuation models, lead scoring, and document intelligence. The system adds geospatial search, market comps, dynamic pricing, and predictive operations to accelerate transactions and improve portfolio performance.',
    icon: Building2,
    features: [
      'Automated Valuation Model with live comps and time series trends',
      'Personalized property recommendations for buyers and renters',
      'Geospatial search with map layers, geofencing, and amenity scoring',
      'Image intelligence for photo quality, room detection, and staging insights',
      'Lead scoring, intent detection, and next best action suggestions',
      'Document intelligence for leases, KYC, and compliance with OCR',
      'Dynamic pricing and rent yield forecasts by micro market',
      'Predictive maintenance, vacancy risk, and churn probability for portfolios',
      'Conversational assistant for listings, tours, and tenant support',
      'Integrations with MLS, CRM, payments, and e signature tools'
    ],
    benefits: [
      'Improves pricing accuracy and time to close',
      'Increases conversion with relevant recommendations',
      'Reduces manual review through automated document processing',
      'Enhances asset performance with predictive insights',
      'Provides transparent analytics for teams and investors'
    ],
    path: '/products/real-estate',
    gradient: 'from-sky-500 to-indigo-700',
    imageUrl: '/assets/Products/ai-Products/RealEstateAI.png',
    imageAlt: 'AI real estate dashboard with map search, comps, and recommended properties'
  },
  {
    id: 'styleSync',
    title: 'StyleSync AI Fashion Styling',
    subtitle: 'AI Fashion Styling Engine',
    description:
      'StyleSync AI is a styling engine that delivers personalized outfit recommendations, virtual try on, and wardrobe analytics for fashion e commerce, brands, and corporate clients. It integrates AI powered styling with virtual try on and analytics to create a seamless shopping experience and scalable solutions.',
    icon: Shirt,
    logo: '',
    features: [
      'Personalized outfit recommendations based on preferences and context',
      'Virtual try on for instant visual feedback',
      'Wardrobe analytics for gaps, usage, and mix and match suggestions',
      'Real time integration with fashion retailers for current product availability',
      'Seamless camera interface for quick style capture and analysis',
      'Scalable deployment for brands, marketplaces, and corporate programs'
    ],
    benefits: [
      'Improves user experience with personalized styling',
      'Increases engagement and conversion for fashion e commerce',
      'Makes professional styling accessible to everyday users',
      'Supports inclusive fashion across diverse body types and cultures'
    ],
    path: '/products/style-sync',
    gradient: 'from-green-500 to-yellow-600',
    imageUrl: '/assets/Products/ai-Products/styleSync.png',
    imageAlt: 'AI fashion styling app showing outfit recommendations and virtual try on'
  },
  /* hidden – Tawjih x AI
  {
    id: 'tawjihAi',
    title: 'Tawjih AI Banking Recommender',
    subtitle: 'Real Time AI Engagement Engine',
    description:
      'Tawjih AI is a real time AI engine that scores customer data to surface tailored financial products. It automates sales tasks and feeds a unified dashboard, increasing conversion and reducing operational cost for banks. The platform enhances customer engagement and scales with the evolving needs of financial institutions.',
    icon: Gauge,
    logo: '',
    features: [
      'Real time scoring of customer profiles and behaviors',
      'Personalized product recommendations across channels',
      'Automated sales workflows and task assignment',
      'Unified dashboard with customer insights and next best actions',
      'Integration with core banking, CRM, and marketing tools',
      'Audit ready logs and configurable compliance controls'
    ],
    benefits: [
      'Higher conversion through timely and relevant offers',
      'Lower operational cost with automation and fewer manual steps',
      'Improved customer satisfaction and loyalty',
      'Better decision making with actionable insights',
      'Scalable architecture for enterprise banking needs'
    ],
    path: '/products/ai/tawjihai',
    gradient: 'from-green-500 to-emerald-600',
    imageUrl: '/assets/Products/ai-Products/TawjihxAI.jpg',
    imageAlt: 'AI banking recommender showing real time scoring, offers, and engagement dashboard'
  },
  */
  {
    id: 'whispeRAGon',
    title: 'WhispeRAGon Digital Human',
    subtitle: 'Cloud Native Multilingual Avatar',
    description:
      'WhispeRAGon is a cloud native, multilingual digital human that combines open source LLMs, Whisper speech recognition, Ollama text generation, viseme based lip sync, and RAG memory to deliver domain specific answers. The avatar supports spoken and typed queries and can switch between industries by uploading relevant data.',
    icon: UserSquare2,
    logo: '',
    features: [
      'Multilingual understanding and responses for speech and text',
      'Viseme based lip sync for natural, real time avatar speech',
      'RAG memory for domain specific retrieval and accurate answers',
      'Easy vertical switching by uploading industry specific datasets',
      'Cloud native architecture for global, scalable deployment',
      'Supports healthcare, legal, education, real estate, and more'
    ],
    benefits: [
      'Delivers human like assistance that improves user experience',
      'Reduces call center load with accurate self service responses',
      'Scales across teams and regions with cloud native operations',
      'Adapts quickly to new domains without full model retraining'
    ],
    path: '/products/ai/whisperagon',
    gradient: 'from-yellow-500 to-amber-600',
    imageUrl: '/assets/Products/ai-Products/whispeRAGon.png',
    imageAlt: 'Multilingual digital human avatar with lip synced speech and domain aware answers'
  }
];


const FloatingIcon: React.FC<{ icon: React.ComponentType<any>; delay: number }> = ({ icon: Icon, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: [0, 1, 0.7, 1], y: [20, 0, -10, 0], rotate: [0, 5, -5, 0] }}
    transition={{ duration: 4, delay, repeat: Infinity, repeatType: 'reverse' }}
    className="absolute"
  >
    <Icon className="h-5 w-5 text-primary/40 md:h-6 md:w-6" />
  </motion.div>
);

const AIParticleField: React.FC = () => {
  const icons = [Brain, Cpu, Network, Activity, Bot, Zap];
  const positions = [
    { top: '10%', left: '10%' },
    { top: '20%', right: '15%' },
    { top: '40%', left: '5%' },
    { top: '60%', right: '10%' },
    { top: '80%', left: '20%' },
    { top: '15%', left: '50%' },
    { top: '35%', right: '30%' },
    { top: '70%', left: '60%' },
    { top: '25%', right: '50%' },
    { top: '85%', right: '40%' },
    { top: '45%', left: '80%' },
    { top: '65%', left: '30%' },
    { top: '30%', right: '20%' },
    { top: '75%', right: '60%' },
    { top: '55%', left: '40%' },
  ];

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {Array.from({ length: 15 }).map((_, i) => {
        const Icon = icons[i % icons.length];
        const position = positions[i];
        return (
          <div key={i} className="absolute" style={position}>
            <FloatingIcon icon={Icon} delay={i * 0.3} />
          </div>
        );
      })}
    </div>
  );
};

const ProductCard: React.FC<{ product: AIProduct; index: number }> = ({ product, index }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  const Icon = product.icon;

  return (
    <section id={product.id} ref={ref} className="relative scroll-mt-24 py-16 md:py-15 lg:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 80 }}
          transition={{ duration: 0.7, delay: index * 0.05 }}
          className={`grid items-center gap-10 md:gap-14 lg:gap-16 lg:grid-cols-2 ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
        >
          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -80 : 80 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}
          >
            {/* Header */}
            <div className="mb-6 flex items-center gap-4 sm:mb-8">
              <motion.div
                whileHover={{ rotate: product.logo ? 0 : 360, scale: 1.1 }}
                transition={{ duration: 0.6 }}
                className={`neon-glow rounded-xl p-3 sm:p-4 ${product.logo ? 'bg-white/10' : `bg-gradient-to-br ${product.gradient}`}`}
                aria-hidden
              >
                {product.logo ? (
                  <img
                    src={product.logo}
                    alt={`${product.title} logo`}
                    className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                    onError={(e) => {
                      // Fallback to icon if logo fails to load
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.className = `neon-glow rounded-xl p-3 sm:p-4 bg-gradient-to-br ${product.gradient}`;
                        e.currentTarget.style.display = 'none';
                        const iconEl = document.createElement('div');
                        iconEl.innerHTML = '<Icon className="h-6 w-6 text-white sm:h-7 sm:w-7" />';
                        parent.appendChild(iconEl);
                      }
                    }}
                  />
                ) : (
                  <Icon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
                )}
              </motion.div>
              <div>
                <motion.h2
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5, delay: 0.25 }}
                  className="mb-1 text-2xl font-bold text-white sm:text-3xl lg:text-4xl"
                >
                  {product.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className={`text-md font-semibold text-transparent bg-clip-text bg-gradient-to-r ${product.gradient}`}
                >
                  {product.subtitle}
                </motion.p>
              </div>
            </div>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="mb-8 max-w-prose text-base leading-relaxed text-muted-foreground sm:text-lg text-justify"
            >
              {product.description}
            </motion.p>

            {/* Features & Benefits */}
            <div className="mb-8 grid gap-6 md:grid-cols-2 md:gap-8">
              {/* Features */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="glass rounded-xl p-5 sm:p-6"
              >
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <Zap className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold text-white">Key Features</h4>
                </div>
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -16 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                      transition={{ duration: 0.35, delay: 0.45 + idx * 0.08 }}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <span>{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>

              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.45 }}
                className="glass rounded-xl p-5 sm:p-6"
              >
                <div className="mb-3 flex items-center gap-2 sm:mb-4">
                  <TrendingUp className="h-5 w-5 text-secondary" />
                  <h4 className="font-semibold text-white">Benefits</h4>
                </div>
                <ul className="space-y-2">
                  {product.benefits.map((benefit, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ opacity: 0, x: -16 }}
                      animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -16 }}
                      transition={{ duration: 0.35, delay: 0.5 + idx * 0.08 }}
                      className="flex items-start gap-2 text-sm text-muted-foreground"
                    >
                      <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
                      <span>{benefit}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.55 }}
            >
              <Link
                to={product.path}
                className="group inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 font-semibold text-white shadow transition-all duration-300 hover:shadow-2xl hover:shadow-primary/25 sm:px-8 sm:py-4"
              >
                Learn More
                <motion.span whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
                  <ArrowRight className="h-5 w-5" />
                </motion.span>
              </Link>
            </motion.div>
          </motion.div>

          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: index % 2 === 0 ? 80 : -80, rotateY: index % 2 === 0 ? -10 : 10 }}
            animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : { opacity: 0, x: index % 2 === 0 ? 80 : -80, rotateY: index % 2 === 0 ? -10 : 10 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className={`relative perspective-1000 ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}
          >
            <div className="relative group">
              {/* Glow */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className={`absolute -inset-6 rounded-full blur-3xl opacity-20 bg-gradient-to-r ${product.gradient}`}
                aria-hidden
              />

              {/* Main Image */}
              <motion.div
                whileHover={{ scale: 1.03, rotateY: 4, rotateX: 2 }}
                transition={{ duration: 0.35 }}
                className="relative z-10 overflow-hidden rounded-2xl border border-white/5 bg-white/5 shadow-xl backdrop-blur-sm"
              >
                <div className="aspect-square w-full">
                  <ImageWithFallback
                    src={product.imageUrl}
                    fallbackSrc="https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1600&auto=format&fit=crop"
                    alt={product.imageAlt || product.title}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Overlay label */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                  <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6">
                    <motion.div
                      initial={{ opacity: 0, y: 12 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }}
                      transition={{ duration: 0.45, delay: 0.6 }}
                      className="flex items-center gap-3 text-white"
                    >
                      <div className="rounded-lg bg-primary/20 p-2 backdrop-blur-sm">
                        <Bot className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">AI-Powered</p>
                        <p className="text-xs text-white/80 sm:text-sm">Next-Gen Technology</p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export const AIProductsPage: React.FC = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { scrollYProgress } = useScroll({ target: containerRef });
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);

  // Note: Scroll-to-section behavior is now handled globally by ScrollToTop component in App.tsx

  return (
    <div ref={containerRef} className="relative min-h-screen pt-20">
      {/* Animated Background */}
      <motion.div style={{ y: backgroundY }} className="fixed inset-0 opacity-30">
        <AIParticleField />
      </motion.div>

      {/* Hero */}
      <section className="relative flex min-h-[70vh] items-center justify-center sm:min-h-[80vh] lg:min-h-screen">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mb-4 text-4xl font-bold leading-tight sm:text-5xl lg:text-7xl"
            >
              <span
                className="neon-text"
                style={{
                  background: 'linear-gradient(135deg, #00CFFF 0%, #0FFFA0 50%, #8A3FFC 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                AI PRODUCTS
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mb-6 flex items-center justify-center gap-3 sm:mb-8 sm:gap-4"
            >
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent sm:w-24" />
              <Brain className="h-6 w-6 animate-pulse-glow text-primary sm:h-8 sm:w-8" />
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-primary to-transparent sm:w-24" />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.45 }}
              className="mx-auto mb-4 max-w-2xl text-base text-muted-foreground text-center sm:text-lg lg:text-xl"
            >
              Harnessing Intelligence & Transforming Industries
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mx-auto mb-10 max-w-3xl text-sm text-muted-foreground text-center sm:text-base lg:text-lg"
            >
              At amasQIS.ai, we build AI solutions that turn data into decisions, automate complex processes, and enable teams to operate at peak efficiency.
            </motion.p>

            {/* Quick Navigation Pills */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mb-14 flex flex-wrap justify-center gap-3 sm:gap-4"
            >
              {aiProducts.map((product, i) => {
                const Icon = product.icon;
                return (
                  <motion.a
                    key={product.id}
                    href={`#${product.id}`}
                    whileHover={{ scale: 1.04, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.85 + i * 0.06 }}
                    className="glass group rounded-xl px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm hover:glass-strong"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary transition-colors group-hover:text-accent" />
                      <span className="font-medium">{product.title}</span>
                    </div>
                  </motion.a>
                );
              })}
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 1 }} className="flex flex-col items-center gap-2">
              <p className="text-xs text-muted-foreground sm:text-sm">Explore Our AI Solutions</p>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="flex h-10 w-6 justify-center rounded-full border-2 border-primary/50"
                aria-hidden
              >
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="mt-2 h-3 w-1 rounded-full bg-primary"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Product Sections */}
      {aiProducts.map((product, index) => (
        <ProductCard key={product.id} product={product} index={index} />
      ))}

      {/* Bottom CTA */}
      <section className="relative pb-16 pt-6 sm:pb-20 sm:pt-10 lg:pb-24 lg:pt-12">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }}>
            <h2 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-3xl lg:text-4xl">Ready to Transform Your Business with AI?</h2>
            <p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground sm:mb-8 sm:text-base">
              Experience the power of intelligent automation and data-driven insights.
            </p>
            <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-primary to-secondary px-6 py-3 font-semibold text-white shadow transition-all duration-300 hover:shadow-2xl hover:shadow-primary/25 sm:px-8 sm:py-4"
              >
                Get Started Today
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-3 rounded-xl px-6 py-3 font-semibold text-white glass hover:glass-strong sm:px-8 sm:py-4"
              >
                Schedule Demo
                <Eye className="h-5 w-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIProductsPage;